/**
 * Investor FAQs — grouped, plain-English Q&A surfaced on the Dashboard FAQs tab.
 *
 * Categories are deliberately ordered by user intent (most common questions
 * first). Each entry's `q` is a short question and `a` is a clear answer
 * without jargon. Keep answers concise — 1–3 sentences where possible.
 */
import {
  Rocket, Briefcase, TrendingUp, Scale, CalendarDays,
  Receipt, Shield, Lock, Share2,
} from 'lucide-react';

export const faqCategories = [
  {
    key: 'getting-started',
    title: 'Getting Started',
    icon: Rocket,
    questions: [
      {
        q: "How do I sign up \u2014 and what's the difference between Continuity System and Portfolio Clarity?",
        a: 'There are two ways in. Continuity System is the entry tier \u2014 you can sign yourself up with just your name, email and password, then set up your private record of assets and trusted contacts. Portfolio Clarity is the top tier, by invitation only \u2014 it includes everything in Continuity System plus allocation, performance, tax and sharing tools. You can start with Continuity System and move up whenever you\u2019re ready.',
      },
      {
        q: 'What if I need help?',
        a: 'You can watch the short walkthrough videos or request a quick setup call.',
      },
      {
        q: 'Is there a MyInvestFolio mobile app?',
        a: 'Currently MyInvestFolio is a responsive web application, optimized for desktop, tablet, and mobile browsers. A native app may come later.',
      },
      {
        q: 'Can I edit or delete investments or assets later?',
        a: 'Yes. You can update your portfolio at any time.',
      },
    ],
  },
  {
    key: 'your-portfolio',
    title: 'Your Portfolio',
    icon: Briefcase,
    questions: [
      {
        q: 'What is the difference between direct and indirect holdings?',
        a: 'Direct holdings are assets you own personally. Indirect holdings are owned through another structure.',
      },
      {
        q: 'What are "Entities" and when should I use them?',
        a: 'Entities are the legal structures through which you hold investments — e.g., a holding company, a family trust, or a personal investment vehicle. If you own shares in a private company through "Lagos Properties Ltd" rather than personally, you would record that entity once and link the relevant investments to it. This lets you see your true wealth picture, including indirect holdings.',
      },
      {
        q: 'Can I add my investments by uploading CSV files?',
        a: 'Yes. You can upload CSV files with your investment history. Pre-formatted CSV files are available for download in the Settings for different investment types.',
      },
      {
        q: 'What metrics does MyInvestFolio track?',
        a: 'MyInvestFolio records every asset you own in one private, encrypted place \u2014 across institutions, currencies and holding entities. On Portfolio Clarity, this record also drives analytics: total committed investments, direct and indirect portfolio breakdowns, allocation by investment type, by currency, by country, and monthly, quarterly and annual returns \u2014 at portfolio level or drilled down to a single investment.',
      },
      {
        q: 'What happens if I exceed 100 investments?',
        a: 'MyInvestFolio is built to scale. The dashboard and analytics remain responsive, and we offer custom plans for large portfolios.',
      },
      {
        q: 'How does MyInvestFolio handle investments held in different currencies?',
        a: 'You record each investment in its native currency — we support over 150. On the Overview tab, totals are converted to a single display currency using daily-refreshed exchange rates so you see a unified picture. On the "By Currency" view, you see what you actually hold in each currency, with no conversion applied.',
      },
    ],
  },
  {
    key: 'performance',
    title: 'Portfolio Performance & Benchmarks \u2014 Portfolio Clarity',
    icon: TrendingUp,
    questions: [
      {
        q: 'How is my return calculated?',
        a: 'Your return is what your investments are worth today minus what you originally paid for them — straightforward and unchanged. The percentages alongside it (both overall and annualized) use Time-Weighted Return, the same method professional investment managers use. It strips out the effect of when you added or withdrew money, so you can compare your performance fairly against any market index.',
      },
      {
        q: 'Can I compare my performance against the market?',
        a: 'Yes. On the Performance tab, you can compare your portfolio\'s returns against major benchmarks — a global stock index (ACWI), a global bond index (Global Agg), and Gold. You can also build your own custom benchmark mix (e.g., 60% stocks / 30% bonds / 10% gold) to better match your investment style.',
      },
      {
        q: 'What does "Update Portfolio Value" do, and why is it sometimes greyed out?',
        a: 'Pressing it records a fresh snapshot of your portfolio\'s value today, used to build your performance history chart. The button is paused for a period after each update (Monthly, Quarterly or Annually — you choose in the dropdown), to keep your history evenly spaced and avoid noisy data.',
      },
    ],
  },
  {
    key: 'net-worth',
    title: 'Liabilities & Statement of Net Worth — Portfolio Clarity',
    icon: Scale,
    questions: [
      {
        q: 'Why should I track my Liabilities?',
        a: 'Adding mortgages, loans, credit-card balances, margin loans, and any tax owed completes the picture of your true financial position. They flow directly into your Statement of Net Worth — making your figures useful to banks, accountants and your estate planner, not just to you.',
      },
      {
        q: 'What is the Statement of Net Worth, and why isn\'t it converted to a single currency?',
        a: 'It is a currency-by-currency summary of what you own minus what you owe — the same view banks and accountants prefer. Each currency stands alone so the right FX rate can be applied when needed. As an indicative roll-up, we also show a "Home Currency Net Worth" line at the bottom, converted at live exchange rates — but the per-currency view remains the source of truth for filing or audit.',
      },
      {
        q: 'Can I export my Statement of Net Worth?',
        a: 'Yes. You can download it as an Excel spreadsheet or a PDF — useful for sharing with your bank, accountant, or estate planner. Closed positions and related-party holdings are excluded automatically.',
      },
    ],
  },
  {
    key: 'cashflow',
    title: 'Cashflow Calendar \u2014 Portfolio Clarity',
    icon: CalendarDays,
    questions: [
      {
        q: 'What is the Cashflow Calendar?',
        a: 'A forward-looking view of the income and maturities you can expect from your portfolio — bond coupons, T-bill and fixed-deposit maturities, dividends, and fund distributions. You choose the window (3, 6, 9 or 12 months) and switch between a calendar grid and an event list.',
      },
      {
        q: 'How does MyInvestFolio know my dividend dates?',
        a: 'Bond coupons and fixed-income maturities are calculated directly from each holding\'s terms (rate, maturity date, payment frequency). For dividends and distributions, we infer the cadence from your past receipts. Events based on only two prior observations are flagged "Unconfirmed" so you can sanity-check them.',
      },
    ],
  },
  {
    key: 'tax',
    title: 'Tax Data \u2014 Portfolio Clarity',
    icon: Receipt,
    questions: [
      {
        q: 'What is the Tax Data Pack?',
        a: 'A clean, country-agnostic record of every taxable input in your portfolio — your holdings as of period end, all income events (bond coupons, T-bill and FD interest, recorded dividends and distributions), and any positions closed within the period. Download as CSV, Excel or PDF to send to your accountant. It is a reconciliation aid, not a tax computation.',
      },
      {
        q: 'My tax year doesn\'t start in January — can I change it?',
        a: 'Yes. The Tax Data Pack supports any tax year. Pick a preset for common jurisdictions (UK, US, Nigeria, etc.) or set a custom start month and day. The system automatically sizes the pack to your last completed tax year.',
      },
      {
        q: 'What is the Current Tax Year Position?',
        a: 'A live snapshot of where your tax year is sitting right now, with three panels: Realised so far (income events and sales already booked), Expected before year-end (high-confidence projections like upcoming coupons and maturities), and Unrealised gain or loss (what would crystallise if you sold each holding today). A planning lens for year-end decisions.',
      },
      {
        q: 'Does the Current Tax Year Position calculate the tax I owe?',
        a: 'No, deliberately. We don\'t apply tax rates or jurisdiction-specific rules — your accountant remains responsible for the actual filing. We give you the raw inputs and timing so you can plan year-end actions (e.g., realising losses, deferring sales) with full clarity.',
      },
    ],
  },
  {
    key: 'estate-vault',
    title: 'Continuity System',
    icon: Shield,
    questions: [
      {
        q: 'Is Continuity System a will or estate service?',
        a: 'No. Continuity System is a private record and access tool — it stores an encrypted picture of your assets and controls who can see it and when. It is not a will, not an estate plan, not a probate service, and does not deal with succession or inheritance law. If you need those services, please speak to a qualified solicitor, estate lawyer, or licensed financial planner in your jurisdiction. What we do sits alongside those services, not in place of them.',
      },
      {
        q: 'What exactly is Continuity System?',
        a: 'Continuity System is our entry tier and also a fully-encrypted feature that ensures your investment information can reach your chosen Trusted Contacts if anything ever happens to you. You decide who your Trusted Contacts are, what they can see, and under what conditions information is released. You retain full control while you are active.',
      },
      {
        q: 'Do my Trusted Contacts need a MyInvestFolio account?',
        a: 'No. When the moment comes and access is released, your Trusted Contacts receive a private link and a short verification step \u2014 a government-issued ID and a live selfie check. They never need to create an account or subscribe to anything.',
      },
      {
        q: 'Can I move from Continuity System up to Portfolio Clarity later?',
        a: 'Yes, any time. Your existing record \u2014 assets, Trusted Contacts and uploaded documents \u2014 carries over exactly as it is. You simply gain the additional analytical view (allocation, performance, tax data, sharing) on top.',
      },
      {
        q: 'What are Continuity Assets and how are they different from my investments?',
        a: 'Continuity Assets are the personal items your Trusted Contacts will need to find \u2014 automobiles, life insurance policies, private jets, and yachts. They sit alongside your Portfolio Assets (investments) inside the Continuity System, but they are recorded separately because they are not investment classes and they do not feed portfolio allocation or performance metrics. Each item can carry attached documents (titles, insurance certificates, loan agreements) and can optionally roll into your Statement of Net Worth on Portfolio Clarity.',
      },
      {
        q: 'How are Trusted Contacts verified before they can access my information?',
        a: 'When a Trusted Contact is invited to begin the information-transfer process, they must upload a government-issued ID, a death or incapacitation certificate, and proof of their relationship to you. They also complete a live selfie check against their ID document. Two MyInvestFolio administrators must independently approve the request before any information is released. Nothing happens automatically.',
      },
      {
        q: 'What happens if I become unavailable?',
        a: 'A calm, multi-stage safety net called the Continuity Trigger. Alongside the Quiet Check-in Cycle (a short "I am well" email you can respond to with one click every 90 days), if you stop logging in and stop responding for an extended period, gentle reminders escalate slowly over months. Only after several missed check-ins — and only after two MyInvestFolio administrators independently review the situation — does the Continuity System progression begin. Nothing happens automatically, and you can pause or cancel the whole cycle at any time, instantly.',
      },
      {
        q: 'Can I cancel or revoke my Continuity System setup?',
        a: 'Yes, at any time and instantly. Go to Settings → Continuity System. Pausing or revoking takes effect immediately and is fully reversible.',
      },
    ],
  },
  {
    key: 'security',
    title: 'Security & Privacy',
    icon: Lock,
    questions: [
      {
        q: 'How do you protect my data?',
        a: 'Your investment data is encrypted at the field level using a key derived from your password — meaning even MyInvestFolio engineers cannot read your portfolio. We never send your data to outside AI tools, never share it with marketers, and never sell it. Your password is never stored — only a salted, irreversible hash.',
      },
      {
        q: 'What is Two-Factor Authentication (2FA), and should I enable it?',
        a: 'Two-Factor Authentication adds a second layer of security: after entering your password, you will be asked for a 6-digit code from an authenticator app (e.g., Google Authenticator, Authy, 1Password). We recommend enabling it from Settings → Security — especially for accounts with substantial portfolios.',
      },
      {
        q: 'How does sharing work if you can\'t see my data?',
        a: 'When you create a shareable view, we generate an encrypted token that grants limited, read-only access. The recipient\'s browser decrypts the data locally — our servers never see the decrypted content.',
      },
    ],
  },
  {
    key: 'sharing-exports',
    title: 'Optional Sharing & Exports \u2014 Portfolio Clarity',
    icon: Share2,
    questions: [
      {
        q: 'How do I share my portfolio with my advisor or other trusted professional?',
        a: 'Go to the "Sharing" section, choose which investments to share, set permissions (view only), and generate a secure link. Your trusted professional will see exactly what you\'ve allowed, with no ability to edit.',
      },
      {
        q: 'Can I share only part of my portfolio?',
        a: 'Yes. You can select individual funds, direct deals, or syndicates. You control exactly what each collaborator sees.',
      },
      {
        q: 'Can I generate reports for tax or estate planning?',
        a: 'Yes. You can export portfolio summaries and transaction histories for use with your tax professional or estate planner.',
      },
    ],
  },
];

// Flat list (for searching across all categories).
export function flattenFaqs() {
  const out = [];
  faqCategories.forEach((cat) => {
    cat.questions.forEach((qa, idx) => {
      out.push({ catKey: cat.key, catTitle: cat.title, qIdx: idx, ...qa });
    });
  });
  return out;
}
